import { useEffect, useState } from "react";
import { Layout } from "../components/Layout";
import { Panel } from "../components/Panel";
import { fetchRuntimeWorkspace, type RuntimeWorkspaceOverview } from "../lib/api/runtimeWorkspaceClient";
import { useWorkspaceLanguage } from "../lib/i18n";
import { readBilingualText } from "../lib/i18n/bilingualContent";
import { getPackageLabel } from "../lib/packageLimits";

export function WorkspaceSetup() {
  const language = useWorkspaceLanguage();
  const copy = language === "en" ? {
    title: "Company data and objective",
    description: "Company Owners see the core information for their workspace here.",
    loadingTitle: "Workspace is loading",
    loadingBody: "Workspace data is loading...",
    loadError: "Workspace data could not be loaded.",
    errorTitle: "Workspace could not be loaded",
    noWorkspaceTitle: "No workspace available yet.",
    noWorkspaceBody: "There is currently no workspace stored for your access.",
    package: "Package",
    objective: "Objective",
    noObjective: "No objective has been stored yet."
  } : {
    title: "Unternehmensdaten und Zielsetzung",
    description: "Company Owner sehen hier die grundlegenden Angaben für den eigenen Workspace.",
    loadingTitle: "Workspace wird geladen",
    loadingBody: "Workspace-Daten werden geladen...",
    loadError: "Workspace-Daten konnten nicht geladen werden.",
    errorTitle: "Workspace konnte nicht geladen werden",
    noWorkspaceTitle: "Noch kein Workspace vorhanden.",
    noWorkspaceBody: "Für Ihren Zugang ist aktuell noch kein Workspace in D1 hinterlegt.",
    package: "Paket",
    objective: "Zielsetzung",
    noObjective: "Noch keine Zielsetzung hinterlegt."
  };
  const [overview, setOverview] = useState<RuntimeWorkspaceOverview>({ departments: [] });
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | undefined>();

  useEffect(() => {
    let isMounted = true;

    async function loadWorkspace() {
      setIsLoading(true);
      setError(undefined);
      try {
        const nextOverview = await fetchRuntimeWorkspace();
        if (isMounted) setOverview(nextOverview);
      } catch (caught) {
        if (isMounted) setError(caught instanceof Error ? caught.message : copy.loadError);
      } finally {
        if (isMounted) setIsLoading(false);
      }
    }

    void loadWorkspace();

    return () => {
      isMounted = false;
    };
  }, [copy.loadError]);

  const workspace = overview.workspace;

  return (
    <Layout
      eyebrow="Setup"
      title={copy.title}
      description={copy.description}
    >
      {isLoading ? <Panel title={copy.loadingTitle}><p>{copy.loadingBody}</p></Panel> : null}
      {error ? (
        <Panel title={copy.errorTitle} variant="accent">
          <p className="form-error" role="alert">{error}</p>
        </Panel>
      ) : null}
      {!isLoading && !error && !workspace ? (
        <Panel title={copy.noWorkspaceTitle} variant="accent">
          <p>{copy.noWorkspaceBody}</p>
        </Panel>
      ) : null}
      {workspace ? (
        <Panel title="Organization Profile" variant="accent">
          <div className="data-list">
            <div>
              <span>Organization</span>
              <strong>{workspace.organizationName}</strong>
            </div>
            <div>
              <span>Workspace</span>
              <strong>{workspace.name}</strong>
            </div>
            <div>
              <span>{copy.package}</span>
              <strong>{getPackageLabel(workspace.packageType, language)}</strong>
            </div>
            <div>
              <span>{copy.objective}</span>
              <strong>{readBilingualText(workspace.goalI18n ?? workspace.goal, language) || copy.noObjective}</strong>
            </div>
          </div>
        </Panel>
      ) : null}
    </Layout>
  );
}
